import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { CasinoApi, IntlManager, NotificationManager, SiteConfigManager } from '@atlas/core';
import { BasePopup, FreeSpinDetails, Icon, Loader } from '@atlas/ui';

import history from 'components/Layout/history';

import { ACTIVATED } from './FreeSpinsBonuses';

const FreeSpinDetailsPopup = ({
  data: { freeSpin, imageUrl, linkToGame, localizedInfoByLang },
  id,
}) => {
  const {
    createTime,
    currency,
    freeSpin: { casinoLimit, numberOfSpins, timeToUse },
    freeSpinAmount,
    status,
  } = freeSpin;
  const dateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat', 'DD.MM.YYYY');
  const timeFormat = SiteConfigManager.getConfig('config.config.date.timeFormat', 'HH:mm');
  const [gameName, setGameName] = useState('-');
  const [isLoading, setIsLoading] = useState(true);
  const endTime = createTime + timeToUse;

  const expirationTime = useMemo(() => {
    const formattedDate = dayjs(endTime).format(dateFormat);
    const formattedTime = dayjs(endTime).format(timeFormat);

    return `${formattedDate} ${formattedTime}`;
  }, [dateFormat, endTime, timeFormat]);

  useEffect(() => {
    const gameId = casinoLimit.gameIds[0];

    CasinoApi.fetchGameDetailsV2({ gameId })
      .then(res => {
        setGameName(res.game.name);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const handleClosePopup = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleButtonClick = useCallback(() => {
    handleClosePopup();
    history.push(linkToGame);
  }, [handleClosePopup, linkToGame]);

  return (
    <BasePopup className="freespin-details-popup">
      <button className="close-button" onClick={handleClosePopup} type="button">
        <Icon height={16} name="close" width={16} />
      </button>
      {isLoading ? (
        <Loader />
      ) : (
        <FreeSpinDetails
          activated={status === ACTIVATED}
          amount={IntlManager.formatMoney(freeSpinAmount)}
          currencies={[currency]}
          description={localizedInfoByLang.description}
          expirationTime={expirationTime}
          gameName={gameName}
          handleButtonClick={handleButtonClick}
          imageUrl={imageUrl}
          name={localizedInfoByLang.name}
          number={numberOfSpins}
          status={status}
          timerTime={endTime}
        />
      )}
    </BasePopup>
  );
};

PropTypes.FreeSpinDetailsPopup = {
  data: PropTypes.shape({
    freeSpin: PropTypes.shape({
      createTime: PropTypes.string,
      currency: PropTypes.string,
      freeSpin: PropTypes.shape({
        casinoLimit: PropTypes.shape({
          gameIds: PropTypes.arrayOf(PropTypes.string),
        }),
        localizedImageInfoList: PropTypes.arrayOf(
          PropTypes.shape({
            imageLink: PropTypes.string,
          }),
        ),
        numberOfSpins: PropTypes.number,
        timeToUse: PropTypes.number,
      }),
      freeSpinAmount: PropTypes.number,
      status: PropTypes.string,
    }),
    imageUrl: PropTypes.string,
    linkToGame: PropTypes.string,
    localizedInfoByLang: PropTypes.shape({
      description: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  id: PropTypes.number.isRequired,
};

export default FreeSpinDetailsPopup;
